import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { setArtifactDictionaryDataId } from "@/features/mapSchema/mapSchemaSlice";

const defaultState = {
  artifactData: "",
  artifactCount: 0,
  selectedArtifactIndex: 0,
  selectedArtifactReference: "",
  artifactReferenceOptions: [],
  autocompleteOptions: [],
  isSearching: false,
  isLoading: false,
  pendingAlert: null,
  pendingValidationAlert: null,
  lastErrorSegment: null,
  validationResult: null,
};
const artifactPreviewSlice = createSlice({
  name: "artifactPreview",
  initialState: {
    ...defaultState,
  },
  reducers: {
    setSelectedArtifactReference: (state, action) => {
      const { selectedArtifactReference } = action.payload;
      state.selectedArtifactReference = selectedArtifactReference;
    },
    setArtifactReferenceOptions: (state, action) => {
      state.artifactReferenceOptions = action.payload;
    },
    setArtifactCount: (state, action) => {
      const { artifactCount } = action.payload;
      state.artifactCount = artifactCount;
    },
    setSelectedArtifactIndex: (state, action) => {
      const { selectedArtifactIndex } = action.payload;
      state.selectedArtifactIndex = selectedArtifactIndex;
    },
    setArtifactData: (state, action) => {
      const { artifactData } = action.payload;
      state.artifactData = artifactData;
    },
    setIsLoading: (state, action) => {
      const { isLoading } = action.payload;
      state.isLoading = isLoading;
    },
    setPendingAlert: (state, action) => {
      const { pendingAlert } = action.payload;
      state.pendingAlert = pendingAlert;
    },
    setPendingValidationAlert: (state, action) => {
      const { pendingValidationAlert } = action.payload;
      state.pendingValidationAlert = pendingValidationAlert;
    },
    setValidationResult: (state, action) => {
      state.validationResult = action.payload;
    },
    setAutocompleteOptions: (state, action) => {
      state.autocompleteOptions = action.payload;
    },
    resetArtifactPreview: (state, _) => {
      const {
        artifactData,
        artifactCount,
        selectedArtifactIndex,
        isLoading,
        selectedArtifactReference,
        artifactReferenceOptions,
        autocompleteOptions,
        isSearching,
        pendingAlert,
        pendingValidationAlert,
        lastErrorSegment,
        validationResult,
      } = defaultState;
      state.artifactData = artifactData;
      state.artifactCount = artifactCount;
      state.selectedArtifactIndex = selectedArtifactIndex;
      state.isLoading = isLoading;
      state.selectedArtifactReference = selectedArtifactReference;
      state.artifactReferenceOptions = artifactReferenceOptions;
      state.autocompleteOptions = autocompleteOptions;
      state.isSearching = isSearching;
      state.pendingAlert = pendingAlert;
      state.pendingValidationAlert = pendingValidationAlert;
      state.lastErrorSegment = lastErrorSegment;
      state.validationResult = validationResult;
    },
    setLastErrorSegment: (state, action) => {
      state.lastErrorSegment = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifactPreview.pending, (state, _) => {
        state.isLoading = true;
      })
      .addCase(fetchArtifactPreview.rejected, (state, _) => {
        state.isLoading = false;
        state.artifactData = "";
      })
      .addCase(fetchArtifactPreview.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(searchArtifactReferences.pending, (state, _) => {
        state.isSearching = true;
      })
      .addCase(searchArtifactReferences.fulfilled, (state, _) => {
        state.isSearching = false;
      })
      .addCase(searchArtifactReferences.rejected, (state, _) => {
        state.isSearching = false;
      });
  },
});

export const showPendingAlert = createAsyncThunk(
  "artifactPreview/dispatchPendingAlert",
  async (_, { dispatch, getState }) => {
    const state = getState();
    const { pendingAlert } = state.tempInfo.artifactPreview;
    if (pendingAlert == null) return;
    dispatch(createAlert(pendingAlert));
    dispatch(setPendingAlert({ pendingAlert: null }));
  }
);

const createError = (dispatch, exception) => {
  let errorMessage = "";
  let segments = [];
  if (exception.response.status === 400) {
    const data = exception.response.data;
    if (typeof data === "object" && data !== null && data.Message) {
      errorMessage = data.Message;
      segments = data.Segments || [];
    } else {
      errorMessage = typeof data === "string" ? data : JSON.stringify(data);
    }
  } else {
    errorMessage = `Unexpected error has occured with code ${exception.response.status}`;
  }

  // Always dispatch the main error alert without segments
  dispatch(
    setPendingAlert({
      pendingAlert: {
        alertType: "result",
        status: "Error",
        title: "Error",
        buttonLabel: "Close",
        message: errorMessage,
        segments: [],
      },
    })
  );

  // If there are validation segments, dispatch them separately 
  if (segments.length > 0) {
    const hasErrors = segments.some(
      (s) => (s.ValidateCode || "").trim().toUpperCase() === "E"
    );
    dispatch(
      setPendingValidationAlert({
        pendingValidationAlert: {
          alertType: "result",
          status: hasErrors ? "Error" : "Warning",
          title: hasErrors ? "Validation Errors" : "Validation Warnings",
          buttonLabel: "Close",
          message: "",
          segments: segments,
        },
      })
    );
  }
};

export const fetchArtifactReferenceOptions = createAsyncThunk(
  "artifactPreview/fetchArtifactReferenceOptions",
  async ({ connectionId, artifactType }, { dispatch }) => {
    try {
      const result = await axios.get(
        "mapschema/outbound/artifact-references",
        {
          params: {
            connectionId,
            artifactType,
          },
        }
      );
      dispatch(setArtifactReferenceOptions(result.data));
    } catch (e) {
      createError(dispatch, e);
    }
  }
);

export const initialiseArtifactPreview = createAsyncThunk(
  "artifactPreview/initialiseArtifactPreview",
  async ({ connectionId, artifactType, variantName }, { dispatch }) => {
    try {
      const artifactCountRes = await axios.get(
        "mapschema/outbound/artifact-count",
        {
          params: {
            connectionId,
            artifactType,
          },
        }
      );
      const numberofArtifacts = Number.parseInt(artifactCountRes.data);
      await dispatch(setArtifactCount({ artifactCount: numberofArtifacts }));
      await dispatch(
        setSelectedArtifactIndex({
          selectedArtifactIndex: Math.max(numberofArtifacts - 1, 0),
        })
      );
      await dispatch(
        fetchArtifactReferenceByIndex({ connectionId, artifactType })
      );
      dispatch(
        fetchArtifactReferenceOptions({ connectionId, artifactType })
      );
      dispatch(
        fetchArtifactPreview({ connectionId, artifactType, variantName })
      );
    } catch (e) {
      createError(dispatch, e);
    }
  }
);
export const incrementAndUpdateArtifactPreview = createAsyncThunk(
  "artifactPreview/incrementAndUpdateArtifactPreview",
  async ({ connectionId, artifactType }, { dispatch, getState }) => {
    const state = getState();
    const { selectedArtifactIndex, artifactCount, artifactData, selectedArtifactReference } =
      state.tempInfo.artifactPreview;
    const newArtifactIndex = selectedArtifactIndex + 1;
    if (newArtifactIndex < artifactCount) {
      await dispatch(setArtifactData({ artifactData: "" }));
      await dispatch(
        setSelectedArtifactIndex({ selectedArtifactIndex: newArtifactIndex })
      );
      const result = await dispatch(
        fetchArtifactReferenceByIndex({ connectionId, artifactType })
      );
      if (result.error) { //We still need to update the index on preview-artifact crash
        await dispatch(setSelectedArtifactIndex({ selectedArtifactIndex }));
        await dispatch(setSelectedArtifactReference({ selectedArtifactReference }));
        await dispatch(setArtifactData({ artifactData }));
      }
    }
  }
);

export const decrementAndUpdateArtifactPreview = createAsyncThunk(
  "artifactPreview/decrementAndUpdateArtifactPreview",
  async ({ connectionId, artifactType }, { dispatch, getState }) => {
    const state = getState();
    const { selectedArtifactIndex, artifactData, selectedArtifactReference } =
      state.tempInfo.artifactPreview;
    const newArtifactIndex = selectedArtifactIndex - 1;
    if (newArtifactIndex >= 0) {
      await dispatch(setArtifactData({ artifactData: "" }));
      await dispatch(
        setSelectedArtifactIndex({ selectedArtifactIndex: newArtifactIndex })
      );
      const result = await dispatch(
        fetchArtifactReferenceByIndex({ connectionId, artifactType })
      );
      if (result.error) { //We still need to update the index on preview-artifact crash
        await dispatch(setSelectedArtifactIndex({ selectedArtifactIndex }));
        await dispatch(setSelectedArtifactReference({ selectedArtifactReference }));
        await dispatch(setArtifactData({ artifactData }));
      }
    }
  }
);

export const fetchArtifactReferenceByIndex = createAsyncThunk(
  "artifactPreview/fetchArtifactReferenceByIndex",
  async ({ connectionId, artifactType }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { selectedArtifactIndex } = state.tempInfo.artifactPreview;
      const result = await axios.get(
        "mapschema/outbound/artifact-reference-by-index",
        {
          params: {
            connectionId,
            artifactType,
            artifactIndex: selectedArtifactIndex,
          },
        }
      );
      const { ArtifactReference, ArtifactDictionaryId } = result.data;
      dispatch(
        setSelectedArtifactReference({
          selectedArtifactReference: ArtifactReference,
        })
      );
      dispatch(
        setArtifactDictionaryDataId({
          artifactDictionaryDataId: ArtifactDictionaryId,
        })
      );
    } catch (e) {
      createError(dispatch, e);
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);
export const fetchArtifactPreview = createAsyncThunk(
  "artifactPreview/fetchArtifactPreview",
  async (
    { connectionId, artifactType, variantName, elements },
    { dispatch, getState }
  ) => {
    try {
      const state = getState();
      const { artifactCount, selectedArtifactReference } =
        state.tempInfo.artifactPreview;

      if (artifactCount === 0 || !selectedArtifactReference) {
        return;
      }

      const result = await axios.post(
        "mapschema/outbound/preview-artifact",
        elements,
        {
          params: {
            connectionId,
            artifactType,
            artifactReference: selectedArtifactReference,
            variantName,
          },
        }
      );

      const {
        EdiPreview,
        ArtifactIndex,
        ArtifactReference,
        ArtifactDictionaryId,
        Validation,
      } = result.data;

      dispatch(setArtifactData({ artifactData: EdiPreview }));

      dispatch(
        setSelectedArtifactIndex({ selectedArtifactIndex: ArtifactIndex })
      );
      dispatch(
        setSelectedArtifactReference({
          selectedArtifactReference: ArtifactReference,
        })
      );
      dispatch(
        setArtifactDictionaryDataId({
          artifactDictionaryDataId: ArtifactDictionaryId,
        })
      );

      // Handle validation results returned alongside the partial preview
      if (Validation && Validation.Segments && Validation.Segments.length > 0) {
        dispatch(setValidationResult(Validation));
        const hasErrors = Validation.Segments.some(
          (s) => (s.ValidateCode || "").trim().toUpperCase() === "E"
        );
        const alertStatus = hasErrors ? "Error" : "Warning";
        dispatch(
          setPendingValidationAlert({
            pendingValidationAlert: {
              alertType: "result",
              status: alertStatus,
              title: hasErrors ? "Validation Errors" : "Validation Warnings",
              buttonLabel: "Close",
              message: Validation.Message || "",
              segments: Validation.Segments,
            },
          })
        );
      } else {
        dispatch(setValidationResult(null));
      }
    } catch (e) {
      const errorData = e?.response?.data;
      if (
        typeof errorData === "object" &&
        errorData !== null &&
        errorData.Segments &&
        errorData.Segments.length > 0
      ) {
        dispatch(setValidationResult({ Message: errorData.Message || "", Segments: errorData.Segments }));
      } else {
        dispatch(setValidationResult(null));
        dispatch(setArtifactData({ artifactData: "" }));
      }
      createError(dispatch, e);
    }
  }
);

export const searchArtifactReferences = createAsyncThunk(
  "artifactPreview/searchArtifactReferences",
  async ({ connectionId, artifactType, searchText, excludeArchived = true }, { dispatch }) => {
    try {
      const result = await axios.get(
        "mapschema/outbound/artifact-references",
        {
          params: {
            connectionId,
            artifactType,
            searchText,
            excludeArchived,
            maxResults: 100,
          },
        }
      );
      dispatch(setAutocompleteOptions(result.data));
    } catch (e) {
      createError(dispatch, e);
    }
  }
);

export const selectArtifactData = (state) =>
  state.tempInfo.artifactPreview.artifactData;
export const selectArtifactCount = (state) =>
  state.tempInfo.artifactPreview.artifactCount;
export const selectSelectedArtifactIndex = (state) =>
  state.tempInfo.artifactPreview.selectedArtifactIndex;
export const selectIsLoading = (state) =>
  state.tempInfo.artifactPreview.isLoading;
export const selectSelectedArtifactReference = (state) =>
  state.tempInfo.artifactPreview.selectedArtifactReference;
export const selectArtifactReferenceOptions = (state) =>
  state.tempInfo.artifactPreview.artifactReferenceOptions;
export const selectAutocompleteOptions = (state) =>
  state.tempInfo.artifactPreview.autocompleteOptions;
export const selectIsSearching = (state) =>
  state.tempInfo.artifactPreview.isSearching;
export const selectPendingAlert = (state) =>
  state.tempInfo.artifactPreview.pendingAlert;
export const selectLastErrorSegment = (state) =>
  state.tempInfo.artifactPreview.lastErrorSegment;
export const selectValidationResult = (state) =>
  state.tempInfo.artifactPreview.validationResult;
export const selectPendingValidationAlert = (state) =>
  state.tempInfo.artifactPreview.pendingValidationAlert;
export const {
  setSelectedArtifactIndex,
  setArtifactCount,
  setArtifactData,
  setPendingAlert,
  setPendingValidationAlert,
  resetArtifactPreview,
  setSelectedArtifactReference,
  setArtifactReferenceOptions,
  setAutocompleteOptions,
  appendArtifactReferenceOption,
  setLastErrorSegment,
  setValidationResult,
} = artifactPreviewSlice.actions;
export default artifactPreviewSlice.reducer;
